/**
 * FleetPage — trucks / trailers / yards / drivers fleet console (Quick 260616-flt / DEC-067).
 *
 * Four tabs. Trucks/Trailers/Yards support add + edit + deactivate.
 *
 * Drivers supports ADD (quick 260814-k6a / ClickUp 86bbed5r5) + edit
 * (shift / home yard / CDL). "New driver" posts to MANAGE_USERS-gated
 * POST /v1/users, which provisions the Cognito user and the users +
 * tenant_users rows together; the driver then signs in by SMS code.
 *
 * Still deferred from DEC-067: deactivate (Cognito AdminDisableUser), yard
 * transfer, and the app-download invite SMS.
 *
 * Fleet mutations are gated server-side by MANAGE_FLEET and user creation by
 * MANAGE_USERS; the Fleet nav is hidden for non-manager roles (App.tsx) as UX
 * only.
 */

import * as React from 'react';
import { toast } from 'sonner';
import { Plus, Pencil } from 'lucide-react';
import { useApiOpts } from '@lodex-pro/web-shared';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Combobox } from '../components/Combobox';
import { fieldStyle, FormRow, labelStyle } from '../features/sites-operators/form-bits';
import {
  useFleetTrucks,
  useFleetTrailers,
  useFleetYards,
  useFleetDrivers,
  useCreateTruck,
  useUpdateTruck,
  useCreateTrailer,
  useUpdateTrailer,
  useCreateYard,
  useUpdateYard,
  useUpdateDriver,
  type TruckSummary,
  type TrailerSummary,
  type YardSummary,
  type FleetDriver,
  type TruckBody,
  type TrailerBody,
  type YardBody,
  type DriverBody,
} from '../features/fleet/hooks';
import { canonicalizePhone, formatPhoneForDisplay } from '@lodex-pro/shared/phone';
import { AddUserForm } from '../features/fleet/AddUserForm';
import type { CreatableUserRole } from '../features/fleet/use-create-user';
import { useTeam, isNotInvited, type TeamMember } from '../features/team/hooks';
import { InviteButton, NotInvitedBadge } from '../features/team/InviteButton';
import { formatNumber } from '@lodex-pro/shared/number-format';
import { useUrlParam } from '../lib/url-state';

type FleetTab = 'drivers' | 'trucks' | 'trailers' | 'yards';
const FLEET_TABS: readonly FleetTab[] = ['drivers', 'trucks', 'trailers', 'yards'];

/** This tab creates drivers only — see the AddButton comment in DriversSection. */
const DRIVER_ONLY_ROLES: readonly CreatableUserRole[] = ['DRIVER'];

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '10px 12px',
  borderBottom: '1px solid var(--color-border-secondary)',
  fontSize: '14px',
};
const subText: React.CSSProperties = { fontSize: '12px', color: 'var(--color-text-tertiary)' };
const searchInputStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '38px',
  padding: '8px 10px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
  marginBottom: '12px',
};
const NONE_OPTION = { value: '', label: '— None —' };

function includesCI(haystack: string | null | undefined, needle: string): boolean {
  return (haystack ?? '').toLowerCase().includes(needle);
}
function numOrNull(s: string): number | null {
  return s.trim() === '' ? null : Number(s);
}
function strOrNull(s: string): string | null {
  return s.trim() === '' ? null : s.trim();
}

// ─── Shared bits ───────────────────────────────────────────────────────────

function TabButton({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '8px 14px',
        fontSize: '14px',
        fontWeight: 600,
        cursor: 'pointer',
        border: 'none',
        borderBottom: active ? '2px solid var(--color-accent-blue)' : '2px solid transparent',
        backgroundColor: 'transparent',
        color: active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
      }}
    >
      {label}
    </button>
  );
}

function AddButton({ label, onClick }: { label: string; onClick: () => void }): React.JSX.Element {
  return (
    <Button
      type="button"
      onClick={onClick}
      style={{
        backgroundColor: 'var(--color-accent-blue)',
        color: 'var(--color-on-accent)',
        borderColor: 'var(--color-accent-blue)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
      }}
    >
      <Plus size={16} aria-hidden="true" /> {label}
    </Button>
  );
}

function EditButton({ onClick }: { onClick: () => void }): React.JSX.Element {
  return (
    <Button
      type="button"
      variant="outline"
      onClick={onClick}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
    >
      <Pencil size={14} aria-hidden="true" /> Edit
    </Button>
  );
}

function StatusToggle({
  isActive,
  pending,
  onToggle,
  activeLabel,
  inactiveLabel,
}: {
  isActive: boolean;
  pending: boolean;
  onToggle: () => void;
  activeLabel: string;
  inactiveLabel: string;
}): React.JSX.Element {
  return (
    <Button type="button" variant="outline" disabled={pending} onClick={onToggle}>
      {pending ? '…' : isActive ? activeLabel : inactiveLabel}
    </Button>
  );
}

function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}): React.JSX.Element {
  return (
    <input
      type="search"
      value={value}
      onChange={(e): void => onChange(e.target.value)}
      placeholder={placeholder}
      style={searchInputStyle}
      aria-label={placeholder}
    />
  );
}

function SectionHeader({
  title,
  count,
  children,
}: {
  title: string;
  count?: number | undefined;
  children?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '8px',
      }}
    >
      <h2 style={{ fontSize: '16px', fontWeight: 700 }}>
        {title}
        {count !== undefined && <span style={subText}> ({formatNumber(count)})</span>}
      </h2>
      {children}
    </div>
  );
}

function ErrorText({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <p role="alert" style={{ ...labelStyle, color: 'var(--color-danger)' }}>
      {children}
    </p>
  );
}

// ─── Drivers (edit-only) ─────────────────────────────────────────────────────

function DriverEditModal({
  editing,
  open,
  onOpenChange,
}: {
  editing: FleetDriver | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const { data: yardsData } = useFleetYards();
  const yards = (yardsData?.yards ?? []).filter((y) => y.isActive);
  const [shift, setShift] = React.useState('DAY');
  const [homeYardId, setHomeYardId] = React.useState('');
  const [cdlNumber, setCdlNumber] = React.useState('');
  const [cdlState, setCdlState] = React.useState('');
  const [cdlExpiresAt, setCdlExpiresAt] = React.useState('');
  const [phoneRaw, setPhoneRaw] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setShift(editing?.shift === 'NIGHT' ? 'NIGHT' : 'DAY');
    setHomeYardId(editing?.homeYardId ?? '');
    setCdlNumber(editing?.cdlNumber ?? '');
    setCdlState(editing?.cdlState ?? '');
    setCdlExpiresAt(editing?.cdlExpiresAt ?? '');
    // Prefill from the canonical stored value; the input renders it as
    // (XXX) XXX-XXXX so the driver's number is recognisable and no "+1" is shown.
    setPhoneRaw(editing?.phone ?? '');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.driverId]);

  const mutation = useUpdateDriver(editing?.driverId ?? '');
  const handleSave = React.useCallback((): void => {
    if (editing === null) return;
    // Only send phone when it actually changed — the server does a Cognito
    // AdminUpdateUserAttributes round-trip for it, so re-sending an unchanged
    // number would rewrite the Cognito attribute on every save of an unrelated
    // field (shift, CDL) for no reason.
    const canonicalPhone = canonicalizePhone(phoneRaw);
    const phoneChanged = phoneRaw.trim() !== '' && canonicalPhone !== (editing.phone ?? '');
    if (phoneRaw.trim() !== '' && canonicalPhone === '') {
      setError('Enter a 10-digit US mobile number.');
      return;
    }
    const body: DriverBody = {
      shift,
      homeYardId: homeYardId === '' ? null : homeYardId,
      cdlNumber: strOrNull(cdlNumber),
      cdlState: cdlState.trim() === '' ? null : cdlState.trim().toUpperCase(),
      cdlExpiresAt: strOrNull(cdlExpiresAt),
      ...(phoneChanged ? { phone: phoneRaw } : {}),
    };
    mutation.mutate(body, {
      onSuccess: () => {
        toast.success('Driver updated');
        onOpenChange(false);
      },
      onError: () => setError('Could not update the driver. Check your inputs and try again.'),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, shift, homeYardId, cdlNumber, cdlState, cdlExpiresAt, mutation]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit driver{editing !== null ? ` — ${editing.driverName}` : ''}</DialogTitle>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Quick 260814-mh2 — until now NO part of the UI could set a driver phone;
              every working number was set by direct AWS CLI intervention. 10-digit
              entry, no "+1" anywhere (86bbed5r5 item 5); the server canonicalizes
              with the SAME shared helper the login screen uses and syncs Cognito. */}
          <FormRow label="Mobile number" htmlFor="drv-phone">
            <input
              id="drv-phone"
              inputMode="tel"
              autoComplete="tel-national"
              placeholder="(555) 123-4567"
              value={
                canonicalizePhone(phoneRaw) !== ''
                  ? formatPhoneForDisplay(canonicalizePhone(phoneRaw))
                  : phoneRaw
              }
              onChange={(e): void => {
                setPhoneRaw(e.target.value);
                setError(null);
              }}
              style={fieldStyle}
            />
            <div style={subText}>
              {'This is the number they sign in with — changing it changes where their '}
              {'login code is sent.'}
            </div>
          </FormRow>
          <FormRow label="Shift" htmlFor="drv-shift">
            <select
              id="drv-shift"
              value={shift}
              onChange={(e): void => setShift(e.target.value)}
              style={fieldStyle}
            >
              <option value="DAY">Day</option>
              <option value="NIGHT">Night</option>
            </select>
          </FormRow>
          <FormRow label="Home yard" htmlFor="drv-yard">
            <Combobox
              id="drv-yard"
              value={homeYardId}
              onChange={setHomeYardId}
              options={[NONE_OPTION, ...yards.map((y) => ({ value: y.id, label: y.name }))]}
              placeholder="No home yard"
              searchPlaceholder="Search yards…"
              emptyText="No yards match."
            />
          </FormRow>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <FormRow label="CDL number" htmlFor="drv-cdl">
              <input
                id="drv-cdl"
                value={cdlNumber}
                onChange={(e): void => setCdlNumber(e.target.value)}
                style={fieldStyle}
              />
            </FormRow>
            <FormRow label="CDL state" htmlFor="drv-cdl-state">
              <input
                id="drv-cdl-state"
                value={cdlState}
                onChange={(e): void => setCdlState(e.target.value)}
                maxLength={2}
                placeholder="OK"
                style={fieldStyle}
              />
            </FormRow>
          </div>
          <FormRow label="CDL expiry" htmlFor="drv-cdl-exp">
            <input
              id="drv-cdl-exp"
              type="date"
              value={cdlExpiresAt}
              onChange={(e): void => setCdlExpiresAt(e.target.value)}
              style={fieldStyle}
            />
          </FormRow>
          {error !== null && <ErrorText>{error}</ErrorText>}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={(): void => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={mutation.isPending}
            onClick={handleSave}
            style={{
              backgroundColor: 'var(--color-accent-blue)',
              color: 'var(--color-on-accent)',
              borderColor: 'var(--color-accent-blue)',
            }}
          >
            {mutation.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DriversSection(): React.JSX.Element {
  const { data, isLoading, isError } = useFleetDrivers();
  const [search, setSearch] = React.useState('');
  const [modal, setModal] = React.useState<{ open: boolean; editing: FleetDriver | null }>({
    open: false,
    editing: null,
  });
  const [addOpen, setAddOpen] = React.useState(false);
  // Phase 20.6e — which drivers have no login yet (imported, not invited).
  // GET /v1/drivers does not carry it and is visible to roles that cannot
  // invite; GET /v1/users is MANAGE_USERS-gated — the same permission the
  // invite needs — so a caller without it simply sees no Invite buttons.
  const { data: teamData } = useTeam(true);
  const uninvited = React.useMemo(() => {
    const m = new Map<string, TeamMember>();
    for (const u of teamData?.users ?? []) if (isNotInvited(u)) m.set(u.id, u);
    return m;
  }, [teamData]);
  const drivers = data?.drivers ?? [];
  const q = search.trim().toLowerCase();
  const filtered =
    q === ''
      ? drivers
      : drivers.filter((d) => includesCI(d.driverName, q) || includesCI(d.cdlNumber, q));
  return (
    <section>
      <SectionHeader title="Drivers" count={drivers.length}>
        {/* Quick 260814-k6a — the create action this tab was built without.
            The file header used to say "Drivers is EDIT-ONLY … driver
            provisioning … a deferred follow-up" (DEC-067); this is that
            follow-up. Role is locked to DRIVER: a "New driver" button inside a
            section titled Drivers must not offer to create a dispatcher. */}
        <AddButton label="New driver" onClick={(): void => setAddOpen(true)} />
      </SectionHeader>
      <SearchBox value={search} onChange={setSearch} placeholder="Search drivers…" />
      {isLoading && <p style={subText}>Loading…</p>}
      {isError && <ErrorText>Could not load drivers.</ErrorText>}
      {filtered.map((d) => {
        const notInvited = uninvited.get(d.driverId);
        return (
          <div key={d.driverId} style={rowStyle}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>
                {d.driverName}{' '}
                <span style={subText}>· {d.shift === 'NIGHT' ? 'Night' : 'Day'}</span>
                {notInvited !== undefined && (
                  <span style={{ marginLeft: 8 }}>
                    <NotInvitedBadge />
                  </span>
                )}
              </div>
              <div style={subText}>
                {[
                  d.yardName,
                  d.cdlNumber != null && d.cdlNumber !== ''
                    ? `CDL ${d.cdlNumber}${d.cdlState != null ? ` (${d.cdlState})` : ''}`
                    : null,
                ]
                  .filter((v) => v != null && v !== '')
                  .join(' · ') || 'No yard / CDL on file'}
              </div>
            </div>
            {notInvited !== undefined && <InviteButton person={notInvited} />}
            <EditButton onClick={(): void => setModal({ open: true, editing: d })} />
          </div>
        );
      })}
      {!isLoading && drivers.length === 0 && <p style={subText}>No active drivers.</p>}
      {!isLoading && drivers.length > 0 && filtered.length === 0 && (
        <p style={subText}>No drivers match “{search}”.</p>
      )}
      <DriverEditModal
        editing={modal.editing}
        open={modal.open}
        onOpenChange={(open): void => setModal((m) => ({ ...m, open }))}
      />
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New driver</DialogTitle>
          </DialogHeader>
          <AddUserForm
            allowedRoles={DRIVER_ONLY_ROLES}
            onCreated={(result): void => {
              setAddOpen(false);
              // The driver signs in by SMS code, so there is no password to pass
              // on — say so plainly rather than leaving the manager wondering
              // what to hand the new hire.
              toast.success(`${result.fullName} added — they can sign in with their phone number`);
            }}
            onCancel={(): void => setAddOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </section>
  );
}

// ─── Trucks ──────────────────────────────────────────────────────────────────

function TruckModal({
  editing,
  open,
  onOpenChange,
}: {
  editing: TruckSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const isEdit = editing != null;
  const { data: yardsData } = useFleetYards();
  const { data: driversData } = useFleetDrivers();
  const { data: trailersData } = useFleetTrailers();
  const yards = (yardsData?.yards ?? []).filter((y) => y.isActive);
  const drivers = (driversData?.drivers ?? []).filter((d) => d.driverRowId != null);
  const trailers = (trailersData?.trailers ?? []).filter((t) => t.isActive);

  const [unitNumber, setUnitNumber] = React.useState('');
  const [truckType, setTruckType] = React.useState('');
  const [accountingClass, setAccountingClass] = React.useState('');
  const [capacity, setCapacity] = React.useState('');
  const [licensePlate, setLicensePlate] = React.useState('');
  const [licenseState, setLicenseState] = React.useState('');
  const [dotNumber, setDotNumber] = React.useState('');
  const [registrationExpiry, setRegistrationExpiry] = React.useState('');
  const [homeYardId, setHomeYardId] = React.useState('');
  const [defaultDriverId, setDefaultDriverId] = React.useState('');
  const [defaultTrailerId, setDefaultTrailerId] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setUnitNumber(editing?.unitNumber ?? '');
    setTruckType(editing?.truckType ?? '');
    setAccountingClass(editing?.accountingClass ?? '');
    setCapacity(editing?.tankCapacityBbl != null ? String(editing.tankCapacityBbl) : '');
    setLicensePlate(editing?.licensePlate ?? '');
    setLicenseState(editing?.licenseState ?? '');
    setDotNumber(editing?.dotNumber ?? '');
    setRegistrationExpiry(editing?.registrationExpiry ?? '');
    setHomeYardId(editing?.homeYardId ?? '');
    setDefaultDriverId(editing?.defaultDriverId ?? '');
    setDefaultTrailerId(editing?.defaultTrailerId ?? '');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.id]);

  const onDone = (): void => {
    toast.success(isEdit ? 'Truck updated' : 'Truck created');
    onOpenChange(false);
  };
  const createMutation = useCreateTruck(onDone);
  const updateMutation = useUpdateTruck(editing?.id ?? '');
  const pending = createMutation.isPending || updateMutation.isPending;

  const handleSave = React.useCallback((): void => {
    if (unitNumber.trim() === '') {
      setError('Unit number is required.');
      return;
    }
    const cap = numOrNull(capacity);
    if (cap !== null && Number.isNaN(cap)) {
      setError('Capacity must be a number.');
      return;
    }
    setError(null);
    const body: TruckBody = {
      unitNumber: unitNumber.trim(),
      truckType: strOrNull(truckType),
      // Sent exactly as typed. This is matched against QuickBooks' own class list,
      // so normalising the case would point the ticket at a DIFFERENT class.
      accountingClass: strOrNull(accountingClass),
      tankCapacityBbl: cap,
      licensePlate: strOrNull(licensePlate),
      licenseState: licenseState.trim() === '' ? null : licenseState.trim().toUpperCase(),
      dotNumber: strOrNull(dotNumber),
      registrationExpiry: strOrNull(registrationExpiry),
      homeYardId: homeYardId === '' ? null : homeYardId,
      defaultDriverId: defaultDriverId === '' ? null : defaultDriverId,
      defaultTrailerId: defaultTrailerId === '' ? null : defaultTrailerId,
    };
    const onError = (): void =>
      setError(
        isEdit
          ? 'Could not update the truck. Check your inputs and try again.'
          : 'Could not create the truck. Check your inputs and try again.',
      );
    if (isEdit) updateMutation.mutate(body, { onSuccess: onDone, onError });
    else createMutation.mutate(body, { onError });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEdit,
    unitNumber,
    truckType,
    accountingClass,
    capacity,
    licensePlate,
    licenseState,
    dotNumber,
    registrationExpiry,
    homeYardId,
    defaultDriverId,
    defaultTrailerId,
    createMutation,
    updateMutation,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit truck' : 'New truck'}</DialogTitle>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <FormRow label="Unit number" htmlFor="trk-unit" required>
              <input
                id="trk-unit"
                value={unitNumber}
                onChange={(e): void => setUnitNumber(e.target.value)}
                placeholder="Truck 14"
                style={fieldStyle}
              />
            </FormRow>
            <FormRow label="Type" htmlFor="trk-type">
              <input
                id="trk-type"
                value={truckType}
                onChange={(e): void => setTruckType(e.target.value)}
                placeholder="e.g. Vacuum / Winch"
                style={fieldStyle}
              />
            </FormRow>
          </div>
          <FormRow label="QuickBooks class" htmlFor="trk-class">
            <input
              className="keep-case"
              id="trk-class"
              value={accountingClass}
              onChange={(e): void => setAccountingClass(e.target.value)}
              placeholder="e.g. Calumet:Tractors:112"
              style={fieldStyle}
            />
            {/* The warning is the point of this field. It is copied into the QBD
                export untouched, and QuickBooks matches it character-for-character
                against its own class list — so a "tidied" value silently files the
                ticket to the wrong cost centre. */}
            <p style={{ ...subText, marginTop: 4 }}>
              Copied into the QuickBooks export exactly as typed, including capitals. Enter it to
              match QuickBooks character-for-character. Leave blank for no class.
            </p>
          </FormRow>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <FormRow label="Capacity (bbl)" htmlFor="trk-cap">
              <input
                id="trk-cap"
                value={capacity}
                onChange={(e): void => setCapacity(e.target.value)}
                inputMode="decimal"
                style={fieldStyle}
              />
            </FormRow>
            <FormRow label="DOT number" htmlFor="trk-dot">
              <input
                id="trk-dot"
                value={dotNumber}
                onChange={(e): void => setDotNumber(e.target.value)}
                style={fieldStyle}
              />
            </FormRow>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <FormRow label="License plate" htmlFor="trk-plate">
              <input
                id="trk-plate"
                value={licensePlate}
                onChange={(e): void => setLicensePlate(e.target.value)}
                style={fieldStyle}
              />
            </FormRow>
            <FormRow label="State" htmlFor="trk-plate-state">
              <input
                id="trk-plate-state"
                value={licenseState}
                onChange={(e): void => setLicenseState(e.target.value)}
                maxLength={2}
                placeholder="OK"
                style={fieldStyle}
              />
            </FormRow>
            <FormRow label="Registration expiry" htmlFor="trk-reg">
              <input
                id="trk-reg"
                type="date"
                value={registrationExpiry}
                onChange={(e): void => setRegistrationExpiry(e.target.value)}
                style={fieldStyle}
              />
            </FormRow>
          </div>
          <FormRow label="Home yard" htmlFor="trk-yard">
            <Combobox
              id="trk-yard"
              value={homeYardId}
              onChange={setHomeYardId}
              options={[NONE_OPTION, ...yards.map((y) => ({ value: y.id, label: y.name }))]}
              placeholder="No home yard"
              searchPlaceholder="Search yards…"
              emptyText="No yards match."
            />
          </FormRow>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <FormRow label="Default driver" htmlFor="trk-driver">
              <Combobox
                id="trk-driver"
                value={defaultDriverId}
                onChange={setDefaultDriverId}
                options={[
                  NONE_OPTION,
                  ...drivers.map((d) => ({
                    value: d.driverRowId as string,
                    label: d.driverName,
                  })),
                ]}
                placeholder="No default driver"
                searchPlaceholder="Search drivers…"
                emptyText="No drivers match."
              />
            </FormRow>
            <FormRow label="Default trailer" htmlFor="trk-trailer">
              <Combobox
                id="trk-trailer"
                value={defaultTrailerId}
                onChange={setDefaultTrailerId}
                options={[
                  NONE_OPTION,
                  ...trailers.map((t) => ({ value: t.id, label: t.unitNumber })),
                ]}
                placeholder="No default trailer"
                searchPlaceholder="Search trailers…"
                emptyText="No trailers match."
              />
            </FormRow>
          </div>
          {error !== null && <ErrorText>{error}</ErrorText>}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={(): void => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={pending}
            onClick={handleSave}
            style={{
              backgroundColor: 'var(--color-accent-blue)',
              color: 'var(--color-on-accent)',
              borderColor: 'var(--color-accent-blue)',
            }}
          >
            {pending
              ? isEdit
                ? 'Saving…'
                : 'Creating…'
              : isEdit
                ? 'Save changes'
                : 'Create truck'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TruckRow({
  truck,
  onEdit,
}: {
  truck: TruckSummary;
  onEdit: (t: TruckSummary) => void;
}): React.JSX.Element {
  const mutation = useUpdateTruck(truck.id);
  const toggle = (): void => {
    mutation.mutate(
      { isActive: !truck.isActive },
      {
        onSuccess: () =>
          toast.success(truck.isActive ? 'Truck marked out of service' : 'Truck reactivated'),
        onError: () => toast.error('Could not update truck'),
      },
    );
  };
  return (
    <div style={{ ...rowStyle, opacity: truck.isActive ? 1 : 0.5 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>
          {truck.unitNumber}
          {truck.truckType != null && truck.truckType !== '' && (
            <span style={subText}> · {truck.truckType}</span>
          )}
        </div>
        <div style={subText}>
          {[
            truck.homeYardName,
            truck.defaultDriverName != null ? `Driver: ${truck.defaultDriverName}` : null,
            truck.defaultTrailerUnit != null ? `Trailer: ${truck.defaultTrailerUnit}` : null,
          ]
            .filter((v) => v != null && v !== '')
            .join(' · ') || 'No assignments'}
          {!truck.isActive && ' · out of service'}
        </div>
      </div>
      <EditButton onClick={(): void => onEdit(truck)} />
      <StatusToggle
        isActive={truck.isActive}
        pending={mutation.isPending}
        onToggle={toggle}
        activeLabel="Out of service"
        inactiveLabel="Activate"
      />
    </div>
  );
}

function TrucksSection(): React.JSX.Element {
  const { data, isLoading, isError } = useFleetTrucks();
  const [search, setSearch] = React.useState('');
  const [modal, setModal] = React.useState<{ open: boolean; editing: TruckSummary | null }>({
    open: false,
    editing: null,
  });
  const trucks = data?.trucks ?? [];
  const q = search.trim().toLowerCase();
  const filtered =
    q === ''
      ? trucks
      : trucks.filter(
          (t) =>
            includesCI(t.unitNumber, q) ||
            includesCI(t.truckType, q) ||
            includesCI(t.licensePlate, q),
        );
  return (
    <section>
      <SectionHeader title="Trucks" count={trucks.length}>
        <AddButton
          label="New truck"
          onClick={(): void => setModal({ open: true, editing: null })}
        />
      </SectionHeader>
      <SearchBox value={search} onChange={setSearch} placeholder="Search trucks…" />
      {isLoading && <p style={subText}>Loading…</p>}
      {isError && <ErrorText>Could not load trucks.</ErrorText>}
      {filtered.map((t) => (
        <TruckRow
          key={t.id}
          truck={t}
          onEdit={(tr): void => setModal({ open: true, editing: tr })}
        />
      ))}
      {!isLoading && trucks.length === 0 && <p style={subText}>No trucks yet.</p>}
      {!isLoading && trucks.length > 0 && filtered.length === 0 && (
        <p style={subText}>No trucks match “{search}”.</p>
      )}
      <TruckModal
        editing={modal.editing}
        open={modal.open}
        onOpenChange={(open): void => setModal((m) => ({ ...m, open }))}
      />
    </section>
  );
}

// ─── Trailers ──────────────────────────────────────────────────────────────

function TrailerModal({
  editing,
  open,
  onOpenChange,
}: {
  editing: TrailerSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const isEdit = editing != null;
  const { data: yardsData } = useFleetYards();
  const { data: trucksData } = useFleetTrucks();
  const yards = (yardsData?.yards ?? []).filter((y) => y.isActive);
  const trucks = (trucksData?.trucks ?? []).filter((t) => t.isActive);

  const [unitNumber, setUnitNumber] = React.useState('');
  const [trailerType, setTrailerType] = React.useState('');
  const [capacity, setCapacity] = React.useState('');
  const [homeYardId, setHomeYardId] = React.useState('');
  const [assignedTruckId, setAssignedTruckId] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setUnitNumber(editing?.unitNumber ?? '');
    setTrailerType(editing?.trailerType ?? '');
    setCapacity(editing?.capacityBbl != null ? String(editing.capacityBbl) : '');
    setHomeYardId(editing?.homeYardId ?? '');
    setAssignedTruckId(editing?.assignedTruckId ?? '');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.id]);

  const onDone = (): void => {
    toast.success(isEdit ? 'Trailer updated' : 'Trailer created');
    onOpenChange(false);
  };
  const createMutation = useCreateTrailer(onDone);
  const updateMutation = useUpdateTrailer(editing?.id ?? '');
  const pending = createMutation.isPending || updateMutation.isPending;

  const handleSave = React.useCallback((): void => {
    if (unitNumber.trim() === '') {
      setError('Unit number is required.');
      return;
    }
    const cap = numOrNull(capacity);
    if (cap !== null && Number.isNaN(cap)) {
      setError('Capacity must be a number.');
      return;
    }
    setError(null);
    const body: TrailerBody = {
      unitNumber: unitNumber.trim(),
      trailerType: strOrNull(trailerType),
      capacityBbl: cap,
      homeYardId: homeYardId === '' ? null : homeYardId,
      assignedTruckId: assignedTruckId === '' ? null : assignedTruckId,
    };
    const onError = (): void =>
      setError(
        isEdit
          ? 'Could not update the trailer. Check your inputs and try again.'
          : 'Could not create the trailer. Check your inputs and try again.',
      );
    if (isEdit) updateMutation.mutate(body, { onSuccess: onDone, onError });
    else createMutation.mutate(body, { onError });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEdit,
    unitNumber,
    trailerType,
    capacity,
    homeYardId,
    assignedTruckId,
    createMutation,
    updateMutation,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit trailer' : 'New trailer'}</DialogTitle>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <FormRow label="Unit number" htmlFor="trl-unit" required>
              <input
                id="trl-unit"
                value={unitNumber}
                onChange={(e): void => setUnitNumber(e.target.value)}
                placeholder="TR-014"
                style={fieldStyle}
              />
            </FormRow>
            <FormRow label="Type" htmlFor="trl-type">
              <input
                id="trl-type"
                value={trailerType}
                onChange={(e): void => setTrailerType(e.target.value)}
                placeholder="e.g. Vacuum / Frac tank"
                style={fieldStyle}
              />
            </FormRow>
          </div>
          <FormRow label="Capacity (bbl)" htmlFor="trl-cap">
            <input
              id="trl-cap"
              value={capacity}
              onChange={(e): void => setCapacity(e.target.value)}
              inputMode="decimal"
              style={fieldStyle}
            />
          </FormRow>
          <FormRow label="Home yard" htmlFor="trl-yard">
            <Combobox
              id="trl-yard"
              value={homeYardId}
              onChange={setHomeYardId}
              options={[NONE_OPTION, ...yards.map((y) => ({ value: y.id, label: y.name }))]}
              placeholder="No home yard"
              searchPlaceholder="Search yards…"
              emptyText="No yards match."
            />
          </FormRow>
          <FormRow label="Assigned truck" htmlFor="trl-truck">
            <Combobox
              id="trl-truck"
              value={assignedTruckId}
              onChange={setAssignedTruckId}
              options={[NONE_OPTION, ...trucks.map((t) => ({ value: t.id, label: t.unitNumber }))]}
              placeholder="No assigned truck"
              searchPlaceholder="Search trucks…"
              emptyText="No trucks match."
            />
          </FormRow>
          {error !== null && <ErrorText>{error}</ErrorText>}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={(): void => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={pending}
            onClick={handleSave}
            style={{
              backgroundColor: 'var(--color-accent-blue)',
              color: 'var(--color-on-accent)',
              borderColor: 'var(--color-accent-blue)',
            }}
          >
            {pending
              ? isEdit
                ? 'Saving…'
                : 'Creating…'
              : isEdit
                ? 'Save changes'
                : 'Create trailer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TrailerRow({
  trailer,
  onEdit,
}: {
  trailer: TrailerSummary;
  onEdit: (t: TrailerSummary) => void;
}): React.JSX.Element {
  const mutation = useUpdateTrailer(trailer.id);
  const toggle = (): void => {
    mutation.mutate(
      { isActive: !trailer.isActive },
      {
        onSuccess: () =>
          toast.success(trailer.isActive ? 'Trailer marked out of service' : 'Trailer reactivated'),
        onError: () => toast.error('Could not update trailer'),
      },
    );
  };
  return (
    <div style={{ ...rowStyle, opacity: trailer.isActive ? 1 : 0.5 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>
          {trailer.unitNumber}
          {trailer.trailerType != null && trailer.trailerType !== '' && (
            <span style={subText}> · {trailer.trailerType}</span>
          )}
        </div>
        <div style={subText}>
          {[
            trailer.homeYardName,
            trailer.capacityBbl != null ? `${formatNumber(trailer.capacityBbl)} bbl` : null,
            trailer.assignedTruckUnit != null ? `Truck: ${trailer.assignedTruckUnit}` : null,
          ]
            .filter((v) => v != null && v !== '')
            .join(' · ') || 'No assignments'}
          {!trailer.isActive && ' · out of service'}
        </div>
      </div>
      <EditButton onClick={(): void => onEdit(trailer)} />
      <StatusToggle
        isActive={trailer.isActive}
        pending={mutation.isPending}
        onToggle={toggle}
        activeLabel="Out of service"
        inactiveLabel="Activate"
      />
    </div>
  );
}

function TrailersSection(): React.JSX.Element {
  const { data, isLoading, isError } = useFleetTrailers();
  const [search, setSearch] = React.useState('');
  const [modal, setModal] = React.useState<{ open: boolean; editing: TrailerSummary | null }>({
    open: false,
    editing: null,
  });
  const trailers = data?.trailers ?? [];
  const q = search.trim().toLowerCase();
  const filtered =
    q === ''
      ? trailers
      : trailers.filter((t) => includesCI(t.unitNumber, q) || includesCI(t.trailerType, q));
  return (
    <section>
      <SectionHeader title="Trailers" count={trailers.length}>
        <AddButton
          label="New trailer"
          onClick={(): void => setModal({ open: true, editing: null })}
        />
      </SectionHeader>
      <SearchBox value={search} onChange={setSearch} placeholder="Search trailers…" />
      {isLoading && <p style={subText}>Loading…</p>}
      {isError && <ErrorText>Could not load trailers.</ErrorText>}
      {filtered.map((t) => (
        <TrailerRow
          key={t.id}
          trailer={t}
          onEdit={(tr): void => setModal({ open: true, editing: tr })}
        />
      ))}
      {!isLoading && trailers.length === 0 && <p style={subText}>No trailers yet.</p>}
      {!isLoading && trailers.length > 0 && filtered.length === 0 && (
        <p style={subText}>No trailers match “{search}”.</p>
      )}
      <TrailerModal
        editing={modal.editing}
        open={modal.open}
        onOpenChange={(open): void => setModal((m) => ({ ...m, open }))}
      />
    </section>
  );
}

// ─── Yards ───────────────────────────────────────────────────────────────────

function YardModal({
  editing,
  open,
  onOpenChange,
}: {
  editing: YardSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const isEdit = editing != null;
  const [name, setName] = React.useState('');
  const [address, setAddress] = React.useState('');
  const [lat, setLat] = React.useState('');
  const [lng, setLng] = React.useState('');
  const [radius, setRadius] = React.useState('500');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setName(editing?.name ?? '');
    setAddress(editing?.address ?? '');
    setLat(editing?.gpsLat != null ? String(editing.gpsLat) : '');
    setLng(editing?.gpsLng != null ? String(editing.gpsLng) : '');
    setRadius(editing?.geofenceRadiusFt != null ? String(editing.geofenceRadiusFt) : '500');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.id]);

  const onDone = (): void => {
    toast.success(isEdit ? 'Yard updated' : 'Yard created');
    onOpenChange(false);
  };
  const createMutation = useCreateYard(onDone);
  const updateMutation = useUpdateYard(editing?.id ?? '');
  const pending = createMutation.isPending || updateMutation.isPending;

  const handleSave = React.useCallback((): void => {
    if (name.trim() === '') {
      setError('Yard name is required.');
      return;
    }
    const latNum = numOrNull(lat);
    const lngNum = numOrNull(lng);
    const radiusNum = numOrNull(radius);
    if (
      (latNum !== null && Number.isNaN(latNum)) ||
      (lngNum !== null && Number.isNaN(lngNum)) ||
      (radiusNum !== null && Number.isNaN(radiusNum))
    ) {
      setError('GPS coordinates and radius must be numbers.');
      return;
    }
    setError(null);
    const body: YardBody = {
      name: name.trim(),
      address: strOrNull(address),
      gpsLat: latNum,
      gpsLng: lngNum,
      ...(radiusNum !== null ? { geofenceRadiusFt: radiusNum } : {}),
    };
    const onError = (): void =>
      setError(
        isEdit
          ? 'Could not update the yard. Check your inputs and try again.'
          : 'Could not create the yard. Check your inputs and try again.',
      );
    if (isEdit) updateMutation.mutate(body, { onSuccess: onDone, onError });
    else createMutation.mutate(body, { onError });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, name, address, lat, lng, radius, createMutation, updateMutation]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit yard' : 'New yard'}</DialogTitle>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <FormRow label="Name" htmlFor="yard-name" required>
            <input
              id="yard-name"
              value={name}
              onChange={(e): void => setName(e.target.value)}
              placeholder="e.g. Elk City Yard"
              style={fieldStyle}
            />
          </FormRow>
          <FormRow label="Address" htmlFor="yard-addr">
            <input
              id="yard-addr"
              value={address}
              onChange={(e): void => setAddress(e.target.value)}
              style={fieldStyle}
            />
          </FormRow>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <FormRow label="GPS latitude" htmlFor="yard-lat">
              <input
                id="yard-lat"
                value={lat}
                onChange={(e): void => setLat(e.target.value)}
                placeholder="35.5912"
                inputMode="decimal"
                style={fieldStyle}
              />
            </FormRow>
            <FormRow label="GPS longitude" htmlFor="yard-lng">
              <input
                id="yard-lng"
                value={lng}
                onChange={(e): void => setLng(e.target.value)}
                placeholder="-98.1234"
                inputMode="decimal"
                style={fieldStyle}
              />
            </FormRow>
            <FormRow label="Geofence radius (ft)" htmlFor="yard-radius">
              <input
                id="yard-radius"
                value={radius}
                onChange={(e): void => setRadius(e.target.value)}
                inputMode="numeric"
                style={fieldStyle}
              />
            </FormRow>
          </div>
          {error !== null && <ErrorText>{error}</ErrorText>}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={(): void => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={pending}
            onClick={handleSave}
            style={{
              backgroundColor: 'var(--color-accent-blue)',
              color: 'var(--color-on-accent)',
              borderColor: 'var(--color-accent-blue)',
            }}
          >
            {pending ? (isEdit ? 'Saving…' : 'Creating…') : isEdit ? 'Save changes' : 'Create yard'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function YardRow({
  yard,
  onEdit,
}: {
  yard: YardSummary;
  onEdit: (y: YardSummary) => void;
}): React.JSX.Element {
  const mutation = useUpdateYard(yard.id);
  const toggle = (): void => {
    mutation.mutate(
      { isActive: !yard.isActive },
      {
        onSuccess: () => toast.success(yard.isActive ? 'Yard deactivated' : 'Yard reactivated'),
        onError: () => toast.error('Could not update yard'),
      },
    );
  };
  const hasGps = yard.gpsLat != null && yard.gpsLng != null;
  return (
    <div style={{ ...rowStyle, opacity: yard.isActive ? 1 : 0.5 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{yard.name}</div>
        <div style={subText}>
          {[
            yard.address,
            hasGps ? `${yard.gpsLat}, ${yard.gpsLng}` : null,
            `${yard.geofenceRadiusFt} ft`,
          ]
            .filter((v) => v != null && v !== '')
            .join(' · ')}
          {!yard.isActive && ' · inactive'}
        </div>
      </div>
      <EditButton onClick={(): void => onEdit(yard)} />
      <StatusToggle
        isActive={yard.isActive}
        pending={mutation.isPending}
        onToggle={toggle}
        activeLabel="Deactivate"
        inactiveLabel="Reactivate"
      />
    </div>
  );
}

function YardsSection(): React.JSX.Element {
  const { data, isLoading, isError } = useFleetYards();
  const [search, setSearch] = React.useState('');
  const [modal, setModal] = React.useState<{ open: boolean; editing: YardSummary | null }>({
    open: false,
    editing: null,
  });
  const yards = data?.yards ?? [];
  const q = search.trim().toLowerCase();
  const filtered =
    q === '' ? yards : yards.filter((y) => includesCI(y.name, q) || includesCI(y.address, q));
  return (
    <section>
      <SectionHeader title="Yards" count={yards.length}>
        <AddButton label="New yard" onClick={(): void => setModal({ open: true, editing: null })} />
      </SectionHeader>
      <SearchBox value={search} onChange={setSearch} placeholder="Search yards…" />
      {isLoading && <p style={subText}>Loading…</p>}
      {isError && <ErrorText>Could not load yards.</ErrorText>}
      {filtered.map((y) => (
        <YardRow key={y.id} yard={y} onEdit={(yd): void => setModal({ open: true, editing: yd })} />
      ))}
      {!isLoading && yards.length === 0 && <p style={subText}>No yards yet.</p>}
      {!isLoading && yards.length > 0 && filtered.length === 0 && (
        <p style={subText}>No yards match “{search}”.</p>
      )}
      <YardModal
        editing={modal.editing}
        open={modal.open}
        onOpenChange={(open): void => setModal((m) => ({ ...m, open }))}
      />
    </section>
  );
}

// ─── Shell ───────────────────────────────────────────────────────────────────

export function FleetPage(): React.JSX.Element {
  useApiOpts();
  // OPEN-191 — the tab lives in `?tab=`, so Back, a refresh and a shared link land on it.
  const [tabParam, setTab] = useUrlParam('tab', 'drivers', []);
  const tab: FleetTab = FLEET_TABS.find((t) => t === tabParam) ?? 'drivers';
  return (
    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div
        style={{
          display: 'flex',
          gap: '4px',
          borderBottom: '1px solid var(--color-border-secondary)',
        }}
      >
        <TabButton
          active={tab === 'drivers'}
          label="Drivers"
          onClick={(): void => setTab('drivers')}
        />
        <TabButton
          active={tab === 'trucks'}
          label="Trucks"
          onClick={(): void => setTab('trucks')}
        />
        <TabButton
          active={tab === 'trailers'}
          label="Trailers"
          onClick={(): void => setTab('trailers')}
        />
        <TabButton active={tab === 'yards'} label="Yards" onClick={(): void => setTab('yards')} />
      </div>
      <div
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          borderRadius: 'var(--radius-card)',
          padding: '16px',
        }}
      >
        {tab === 'drivers' && <DriversSection />}
        {tab === 'trucks' && <TrucksSection />}
        {tab === 'trailers' && <TrailersSection />}
        {tab === 'yards' && <YardsSection />}
      </div>
    </div>
  );
}
